import { ViewIcon } from "@chakra-ui/icons";
import { IconButtonProps } from "@chakra-ui/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useDeleteLearningPath from "../../hooks/api/learning-path/useDeleteLearningPath";
import useGetLearningPathsPaginated from "../../hooks/api/learning-path/useGetLearningPathsPaginated";
import { LearningPathSummary } from "../../models/LearningPath";
import GenericTable from "./GenericTable";

export default function LearningPathsTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    data: learningPaths,
    isPending,
    isError,
    variables,
    mutateAsync: fetchLearningPaths,
  } = useGetLearningPathsPaginated();
  const { mutateAsync: deleteLearningPath, isPending: isDeletionPending } =
    useDeleteLearningPath();

  const onDeleteConfirm = useCallback(
    (learningPath: LearningPathSummary) => {
      deleteLearningPath({ learningPathId: learningPath.id! }).finally(() =>
        fetchLearningPaths({ page: variables?.page ?? 0 }),
      );
    },
    [deleteLearningPath, fetchLearningPaths, variables?.page],
  );

  const extraActions: (learningPath: LearningPathSummary) => IconButtonProps[] =
    useCallback(
      (learningPath) => {
        return [
          {
            onClick: () =>
              navigate(`./${learningPath.id}/progresses/`, {
                state: { learningPathId: learningPath.id },
              }),
            "aria-label": t("common.buttons.see_progresses"),
            icon: <ViewIcon />,
          },
        ];
      },
      [navigate, t],
    );

  return (
    <GenericTable<LearningPathSummary>
      items={learningPaths?.content ?? []}
      headers={[t("tables.name"), t("tables.description")]}
      rowExtractors={[
        (learningPath) => learningPath.name,
        (learningPath) => learningPath.description,
      ]}
      onEditClick={(learningPath) => navigate(`./${learningPath.id}`)}
      onDeleteConfirm={onDeleteConfirm}
      isDeletionPending={isDeletionPending}
      isLoading={isPending}
      isError={isError}
      pagination={learningPaths?.page}
      refetch={fetchLearningPaths}
      extraActions={extraActions}
    />
  );
}
